/**
 * The last two *pages* the user was on, so "back" can be answered by the app rather than by the
 * browser.
 *
 * [WEB-19778] Why not `window.history`: every product opens in a modal that has its own route, and
 * fullscreen modals are pushed onto the browser stack while the rest are not (WEB-17889,
 * WEB-20119). So `window.history.length` counts entries the user never experienced as pages, and
 * the old "go back if the stack grew" check sent people to the tab they opened ENGAGE in. The
 * stack isn't stale, it's polluted, so no reading of it can be trusted.
 *
 * What is trustworthy is the route table: a route is a page only if it says so
 * (`meta.isReturnablePage`). A modal route added later has no such meta, so it is never recorded,
 * and the worst case stays "back goes to the landing page" instead of "back lands on a modal over
 * the wrong page".
 */
export type Page = {
  pathname: string
  search: Record<string, unknown>
}

export const createPageHistory = () => {
  let current: Page | undefined
  let previous: Page | undefined

  return {
    /**
     * Records a page the user is on.
     *
     * Staying on the same pathname replaces the current entry instead of shifting it into
     * `previous`: Explore rewrites `?search=` on every keystroke, and each of those is the same
     * page, not somewhere to send the user back to. So `previous` always means the last
     * *different* page, which is what a back button promises.
     */
    record: (page: Page) => {
      if (current?.pathname === page.pathname) {
        current = page
        return
      }

      previous = current
      current = page
    },

    getPreviousPage: () => previous,

    /** Tests only: the instance below is module state and would leak between cases. */
    reset: () => {
      current = undefined
      previous = undefined
    },
  }
}

export const pageHistory = createPageHistory()
